import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Eye, Heart, Plus, Minus, Star, Truck } from "lucide-react";
import { useI18n } from "../../i18n";
import { useCart } from "../../contexts/CartContext";
import { useWishlist } from "../../contexts/WishlistContext";
import { toast } from "sonner";

export const QuickViewTrigger = ({ product, onOpen }) => (
  <button
    data-testid={`quickview-trigger-${product.slug}`}
    onClick={(e) => { e.preventDefault(); onOpen(product); }}
    className="absolute bottom-3 right-3 w-10 h-10 bg-white text-ink flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0 hover:bg-ink hover:text-white"
    aria-label="Quick view"
  >
    <Eye className="w-4 h-4" />
  </button>
);

const QuickView = ({ product, open, onOpenChange }) => {
  const { t, lang } = useI18n();
  const { add } = useCart();
  const { toggle, has } = useWishlist();
  const [qty, setQty] = useState(1);

  if (!product) return null;
  const name = lang === "nl" ? product.name_nl : product.name_en;
  const desc = lang === "nl" ? product.description_nl : product.description_en;
  const onSale = product.compare_at_price && product.compare_at_price > product.price;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 gap-0 border-0 rounded-none overflow-hidden bg-white" data-testid="quickview-dialog">
        <DialogTitle className="sr-only">{name}</DialogTitle>
        <div className="grid md:grid-cols-2">
          <div className="bg-secondary-warm aspect-[4/5] md:aspect-auto">
            <img src={product.images?.[0]} alt={name} className="w-full h-full object-cover" />
          </div>
          <div className="p-6 md:p-10 flex flex-col">
            <div className="font-label text-ink-muted">{product.sku}</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter font-light mt-1 leading-tight">{name}</h2>
            <div className="flex items-center gap-2 mt-3">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3 h-3 ${i < Math.round(product.rating_avg || 0) ? "fill-terracotta text-terracotta" : "text-black/15"}`} />)}
              <span className="text-xs text-ink-muted">{product.rating_count || 0} reviews</span>
            </div>
            <div className="mt-5 flex items-baseline gap-3">
              <div className="font-display text-3xl">€{product.price.toFixed(2)}</div>
              {onSale && <div className="text-ink-muted line-through text-sm">€{product.compare_at_price.toFixed(2)}</div>}
            </div>
            <p className="mt-4 text-sm text-ink-muted leading-relaxed line-clamp-4">{desc}</p>

            <div className="mt-5 flex items-center gap-2 text-xs text-olive">
              <Truck className="w-4 h-4" /> {lang === "nl" ? "Voor 23:00 besteld, morgen in huis" : "Ordered before 23:00 — delivered tomorrow"}
            </div>

            <div className="mt-auto pt-6 flex items-center gap-3">
              <div className="flex items-center border border-ink">
                <button data-testid="qv-decrement" onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-11 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <div className="w-10 text-center text-sm">{qty}</div>
                <button data-testid="qv-increment" onClick={() => setQty(qty + 1)} className="w-9 h-11 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                data-testid="qv-add-to-cart"
                disabled={product.stock === 0}
                onClick={() => { add(product, qty); toast.success(lang === "nl" ? "Toegevoegd" : "Added to cart"); onOpenChange(false); }}
                className="btn-primary text-sm flex-1"
              >
                {product.stock === 0 ? t("out_of_stock") : t("add_to_cart")}
              </button>
              <button data-testid="qv-wishlist" onClick={() => toggle(product)} className="w-11 h-11 border border-ink flex items-center justify-center hover:bg-secondary-warm">
                <Heart className={`w-4 h-4 ${has(product.product_id) ? "fill-terracotta text-terracotta" : ""}`} />
              </button>
            </div>
            <Link to={`/product/${product.slug}`} onClick={() => onOpenChange(false)} className="mt-3 text-xs font-label text-ink-muted hover:text-ink text-center">{lang === "nl" ? "Volledige details" : "View full details"} →</Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickView;
